import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import { useAtomValue } from "jotai";
import { Button, ErrorState, Title } from "@common/index";
import { Alert } from "@common/Alert";
import { ComponentColor, ComponentSize } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { useMountRead } from "@/hooks/useMountRead";
import { useIsSessionCurrent } from "@/hooks/useSessionSetAtom";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { isProcessingProductsAtom } from "@/store/productAtoms";
import { apiSentence, asApiFailure, wasReported } from "@utils/apiFailure";
import { LEGAL_CLASS_OPTIONS, LegalClassType } from "@models/product";
import type { Product } from "@models/product";
import { NotFoundContent } from "@/Features/Shared/ErrorPages/NotFoundContent";
import { ProductForm } from "../_components/ProductForm";
import {
    productFormSchema,
    toNumber,
    type ProductFormData,
} from "../_helpers/productForm";
import {
    SAVE_BLOCKED_BY_CATEGORIES,
    useProductFormLookups,
} from "../_helpers/useProductFormLookups";
import { ProductImageManager } from "./_components/ProductImageManager";
import { ProductCoaManager } from "./_components/ProductCoaManager";
import { VendorOfferings } from "./_components/VendorOfferings";
import {
    activeBaseOfferings,
    productStockTotal,
} from "./_helpers/vendorOfferings";

// Map a loaded Product into the string-based form fields. EVERY legal class
// that has a checkbox is carried (#431): the update replaces the product's
// whole list with what the form sends, so a class left out here would be
// deleted by any save. A class with NO checkbox — a legacy None (0), or one
// this build doesn't know — is dropped: the admin could neither see nor
// untick it, and the API rejects both, so keeping it would make every save of
// this product fail.
const toFormData = (product: Product): ProductFormData => ({
    name: product.name,
    brand: product.brand ?? "",
    categoryId: String(product.categoryId),
    legalClasses: product.legalClasses
        .map(String)
        .filter((value) =>
            LEGAL_CLASS_OPTIONS.some((option) => option.value === value),
        ),
    description: product.description ?? "",
    upc: product.upc ?? "",
    ean: product.ean ?? "",
    customSku: "",
    manufacturerSku: product.manufacturerSku ?? "",
    msrp: String(product.msrp),
    discountAllowed: product.discountAllowed,
    popularRank:
        product.popularRank != null ? String(product.popularRank) : "",
});

// The form before any product has loaded — the mount default, and what an id
// change or a Retry resets to. EVERY field, blank: react-hook-form's reset
// keeps a registered field's value when the key is absent, and a name field
// still reading the previous product under disabled controls is what this
// exists to clear.
const EMPTY_FORM_VALUES: ProductFormData = {
    name: "",
    brand: "",
    categoryId: "",
    legalClasses: [],
    description: "",
    upc: "",
    ean: "",
    customSku: "",
    manufacturerSku: "",
    msrp: "",
    discountAllowed: false,
    popularRank: "",
};

// AC-09: the API refuses a save whose concurrency stamp is stale — another
// admin changed this product (a save or a delete) after this page read it —
// with a 409. The page answers that with SAVE_CONFLICT_MESSAGE, so useApi's
// generic toast is silenced for that status alone (a network failure, no
// status, keeps it).
const isSaveConflict = (status: number | undefined) => status === 409;

const SAVE_CONFLICT_MESSAGE =
    "Someone else changed this product while you were editing. Reload to see the current version; your unsaved edits on this page will be lost.";

// A save the API refused with 409: the page read it was made from, its
// product, and the API's own sentence for it.
interface RefusedSave {
    read: number;
    productId: string;
    message: string;
}

export const EditProductPage = () => {
    const navigate = useNavigate();
    const { id = "" } = useParams<{ id: string }>();
    const { getProduct, updateProduct } = useProductApi();
    const { categories, categoriesState, retryCategories } =
        useProductFormLookups();
    const isProcessing = useAtomValue(isProcessingProductsAtom);
    // Loading or failed with nothing to pick from — Save waits for the list.
    const categoriesUnavailable = categoriesState !== "ready";

    const [product, setProduct] = useState<Product | null>(null);
    // Whether the form holds the loaded product: false from the moment a read
    // blanks it until that read fills it. A re-read of the same id keeps
    // `product` while the form is blank, so every write control waits on
    // this, not on `product` alone: Save would write the blank fields.
    const [isFormFilled, setIsFormFilled] = useState(false);
    const canEdit = product !== null && isFormFilled;
    const [isActive, setIsActive] = useState(true);
    // AC-09: the stamp the form's values came with, sent back on Save. Set by
    // the page read ONLY — never by refreshProduct: a refresh after an image
    // change may return a stamp another admin's save produced, and adopting
    // it would let this save overwrite theirs unchecked. Image changes do not
    // move the stamp, so the read's stamp still saves after one. A successful
    // save leaves the page, so the next save is from a fresh read.
    const [loadedStamp, setLoadedStamp] = useState<string | undefined>();
    // How many page reads have started. A save notes the count as it starts,
    // and a 409 records the read and product that save was made from
    // (refusedSave). The conflict holds only while those are still the
    // page's: any page read starting (Reload, an id or session change) ends
    // it. Derived during render, so it never paints over another product or
    // a newer read, not even for one commit. A failed read hands the page to
    // the couldn't-load cell. Save is held during a conflict, since the same
    // stamp would only be refused again.
    const [pageReadsStarted, setPageReadsStarted] = useState(0);
    const [refusedSave, setRefusedSave] = useState<RefusedSave | null>(null);
    const hasSaveConflict =
        refusedSave !== null &&
        refusedSave.read === pageReadsStarted &&
        refusedSave.productId === id;

    // A 409 that is no longer current when it lands (the admin moved to
    // another product, or a newer page read began) answers a save this page
    // no longer shows, so it gets no alert. It must not be silent either —
    // useApi would have toasted it, and its toast is suppressed for 409 — so
    // it is told as useApi tells any other 409: "Error 409" with the API's
    // own sentence. Each refusal is judged ONCE, as it lands (the ref): one
    // that was current there showed the alert, and a later Reload or move
    // that makes it stale must not tell it again.
    const { showError } = useNotification();
    const isSessionCurrent = useIsSessionCurrent();
    const judgedRefusal = useRef<RefusedSave | null>(null);
    useEffect(() => {
        if (refusedSave === null || refusedSave === judgedRefusal.current) {
            return;
        }
        judgedRefusal.current = refusedSave;
        if (!hasSaveConflict) {
            showError({ title: "Error 409", message: refusedSave.message });
        }
    }, [refusedSave, hasSaveConflict, showError]);
    // WHICH id 404'd, not a boolean (same reasoning as ProductDetail): the
    // route has no `key`, so a `:id` change re-uses this mount, and a boolean
    // would show "not found" over the new product for the whole of its read.
    // Comparing ids is derived state — false on the render the id changes.
    // Read only together with readStatus (isNotFound, below).
    const [notFoundProductId, setNotFoundProductId] = useState<string | null>(
        null,
    );

    // The route has no `key`, so an `:id` change re-uses this mount — and
    // while the new product's read is in flight the previous product must not
    // sit in the form with Save enabled, or a click writes p1's content to p2.
    // Reset during render, not in an effect: an effect is a commit too late.
    // Keyed on a string, so `!==` is safe (see useBrowseSubcategory for the
    // NaN caveat on numeric params).
    const [renderedId, setRenderedId] = useState(id);
    if (renderedId !== id) {
        setRenderedId(id);
        setProduct(null);
    }

    // The route id this mount is on NOW, for refreshProduct (PR 371 review
    // 1). Updated in the commit that renders a new id, before any later
    // callback runs (useMountRead keeps its `read` the same way).
    const currentId = useRef(id);
    useLayoutEffect(() => {
        currentId.current = id;
    }, [id]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        getValues,
        setValue,
        formState: { errors },
    } = useForm<ProductFormData>({
        resolver: zodResolver(productFormSchema),
        defaultValues: EMPTY_FORM_VALUES,
    });

    // The category list can arrive AFTER the product — a slow read, or a
    // Retry of a failed one. `reset` then wrote the product's categoryId into
    // a select that had no matching <option> yet, and a select does not take
    // up a value it was given before the option existed: the form keeps the
    // id while the control displays something else. So whenever the list
    // changes, the FORM's current value is written to the control again —
    // the form's, not the loaded product's, so a choice the admin has made
    // since is what gets re-applied.
    useEffect(() => {
        setValue("categoryId", getValues("categoryId"));
    }, [categories, getValues, setValue]);

    // includeInactive (#294): this is the admin surface, and a deactivated
    // product's edit page is the only place it can be reactivated from — the
    // default read 404s on it.
    // skipErrorNotification: both failure outcomes of this read have a
    // designed cell below (not-found / ErrorState), so the generic
    // `Error 404` toast would only double up on them.
    // Also the Retry path: unlike refreshProduct below, this one resets the
    // form, because there is nothing loaded yet to preserve.
    //
    // Only the LATEST read settles into state. getProduct has no request guard
    // of its own (it publishes to no atom and resolves every caller's
    // promise), so without `isCurrent` an older read that settles last (two
    // image mutations back to back, an id change mid-read, or a return to an
    // id whose first read is still out) would write its stale product — or
    // its stale 404 — over the newer one. refreshProduct takes its turn from
    // the same numbering (useMountRead's runSideRead), so the newest of the
    // two is the one kept.
    const readProduct = useCallback((isCurrent: () => boolean) => {
        if (!id) {
            return Promise.resolve();
        }
        // Nothing is loaded for THIS id yet (a mount, a Retry, an id change),
        // so the form goes back to blank rather than holding the previous
        // product's values under the disabled controls.
        reset(EMPTY_FORM_VALUES);
        setIsFormFilled(false);
        // An earlier read's 404 is not this read's outcome. Cleared here, as
        // the read starts, because this read may never be allowed to write
        // its own (a newer read can overtake it), and its failure must not
        // then show the earlier "not found".
        setNotFoundProductId(null);
        // A conflict belongs to the read whose stamp was refused, which this
        // read supersedes (pageReadsStarted), and a 409 still out lands
        // stale. If the read fails, the failed cells take the page over.
        setPageReadsStarted((started) => started + 1);
        return getProduct(id, {
            includeInactive: true,
            skipErrorNotification: true,
        }).then(
            (loaded) => {
                if (!isCurrent()) return;
                setProduct(loaded);
                setLoadedStamp(loaded.concurrencyStamp);
                setIsActive(loaded.isActive);
                reset(toFormData(loaded));
                setIsFormFilled(true);
            },
            (error: unknown) => {
                if (isCurrent()) {
                    setNotFoundProductId(
                        asApiFailure(error)?.status === 404 ? id : null,
                    );
                }
                // Rethrown so useMountRead reports "failed"; a 404 is told
                // apart by notFoundProductId, checked first below.
                throw error;
            },
        );
    }, [id, getProduct, reset]);

    // The outcome of the read THIS mount issued (#407), never an app-scoped
    // error flag: one outlives the page, so a stale failure from an earlier
    // detail read would paint ErrorState + Retry over a page that is loading
    // fine. The hook issues the read on mount and on an :id change,
    // reports "loading" for every read (so a Retry shows that it is working),
    // and settles only from the latest one.
    const {
        readStatus,
        reload: retryProduct,
        runSideRead,
    } = useMountRead(readProduct);
    // The id alone is not enough: notFoundProductId outlives its read. After
    // p1 404s, a move to p2 and back to p1 before p2 answers would match it
    // again and paint "not found" over the new p1 read's first frame. So the
    // CURRENT read must have failed too — readStatus is "loading" on the
    // render a new read starts.
    const isNotFound = notFoundProductId === id && readStatus === "failed";

    // Re-fetch after an image mutation so the grid reflects persisted state,
    // and after a COA change (#476) so its row does.
    // Only the product (and thus its images) is refreshed — the form is NOT
    // reset here, so in-progress field edits are preserved. A failure keeps
    // the loaded form and is left to useApi's toast (no skip here): the
    // admin's edits are worth more than a retry screen, but the failure must
    // still be visible.
    // The image manager calls the onChanged it captured when its mutation
    // started, so a refresh can arrive for an id the route has since left.
    // It is refused: its GET would be the newest read and put that product
    // on this page.
    // A refresh never lands on a blank form, and never overtakes a read in
    // flight (PR 378 review): its mutation started while the form was filled,
    // and a read can start after that only through a change of `readProduct`
    // (an id, session or destination change), whose runSideRead then refuses
    // this refresh, or through a reload of the same read. A Retry needs a
    // failed read, which a filled form has not. Reload product (after a 409)
    // is shown on a filled form, so it is held while an image write is
    // processing (PR 409 review 1): a reload under the write's refresh would
    // be overtaken by it, its answer dropped, and the page left blank and
    // locked while reporting "loaded".
    // The COA manager is the exception (PR 422 review 1): it calls the
    // refresh of the latest render, so after p1 → p2 → p1 its write can land
    // while p1's new read is still out, and that read may have been answered
    // from before the write. The page read is issued again instead of a side
    // read: the form is blank anyway, and the new read fills it.
    const refreshProduct = useCallback(() => {
        if (!id || id !== currentId.current) {
            return;
        }
        if (readStatus === "loading") {
            void retryProduct();
            return;
        }
        void runSideRead(async (isCurrent) => {
            const loaded = await getProduct(id, { includeInactive: true });
            if (!isCurrent()) return;
            setProduct(loaded);
        });
    }, [id, getProduct, runSideRead, readStatus, retryProduct]);

    // The image endpoints 404 on an inactive product until reactivation is
    // SAVED (#294), so the gate is the loaded product's state, not the local
    // toggle — flipping the switch does nothing server-side until Save.
    const isInactive = product !== null && !product.isActive;

    // A 409 (isSaveConflict) keeps the admin here with their edits and shows
    // the conflict alert, or the late-409 toast if a page read has started
    // since the save did (refusedSave). A 409 for a session that has since
    // ended is dropped, as useApi drops every notice for one. The fallback
    // sentence is the HTTP status text, as useApi's toast would use for an
    // empty body. Any other failure useApi has already shown (a toast)
    // also keeps them here, as before, and is not rethrown out of the submit
    // handler, where nothing would catch it; a failure useApi did not report
    // still throws.
    const onSave = handleSubmit(async (values) => {
        const readsAtSave = pageReadsStarted;
        try {
            await updateProduct(
                id,
                {
                    categoryId: Number(values.categoryId),
                    name: values.name.trim(),
                    description: values.description.trim(),
                    upc: values.upc.trim(),
                    ean: values.ean?.trim() ?? "",
                    manufacturerSku: values.manufacturerSku?.trim() ?? "",
                    brand: values.brand.trim(),
                    // No cost or price (P1/P2): the API derives both from the
                    // vendor offers.
                    msrp: toNumber(values.msrp),
                    discountAllowed: values.discountAllowed ?? false,
                    // Tags are required by the API but have no field in this
                    // form — preserve the loaded value so a save does not wipe
                    // them.
                    // PiecesPerUnit is not sent (AC-16): GetProduct reports the
                    // BASE SKU's value, so echoing it wrote the sku's value onto
                    // the product. Omitted, the API keeps the stored value.
                    tags: product?.tags ?? [],
                    isActive,
                    // Empty input → null (unpin). GetProduct returns the
                    // current rank (E4-S4), so editing other fields preserves
                    // an existing pin.
                    popularRank: values.popularRank?.trim()
                        ? Number(values.popularRank)
                        : null,
                    // The full list — the API replaces the product's classes
                    // with it.
                    legalClasses: values.legalClasses.map(
                        (value) => Number(value) as LegalClassType,
                    ),
                    concurrencyStamp: loadedStamp,
                },
                { skipErrorNotification: isSaveConflict },
            );
        } catch (error) {
            const failure = asApiFailure(error);
            if (isSaveConflict(failure?.status)) {
                if (isSessionCurrent()) {
                    setRefusedSave({
                        read: readsAtSave,
                        productId: id,
                        message: apiSentence(failure?.detail) ?? "Conflict",
                    });
                }
                return;
            }
            if (wasReported(error)) {
                return;
            }
            throw error;
        }
        navigate(ROUTES.ADMIN_PRODUCTS);
    });

    // Five cells, checked in this order:
    //
    //   1 failed with 404 for THIS id       → not found, with a way back to
    //                                         the product list
    //   2 failed otherwise                  → ErrorState + Retry, no form
    //   3 loading (form not yet filled)     → the form with every field, the
    //                                         Active toggle, image controls
    //                                         and Save disabled — silent about
    //                                         inactivity. Nothing is editable
    //                                         until the read lands, because
    //                                         `reset` on load would discard it.
    //   4 loaded, active                    → the form
    //   5 loaded, inactive                  → the form, image controls disabled
    //                                         with the reason line
    //
    // The cells show the outcome of the LATEST read (useMountRead's
    // readStatus). Each read clears notFoundProductId as it starts, and only a
    // read still allowed to write sets it, so a superseded read never sets or
    // clears it. Cell 2 does not wait for the product to be gone: a re-read of
    // the same id (the read changes with the session) keeps the loaded
    // product but has already blanked the form, so its failure is a
    // couldn't-load like any other.

    if (isNotFound) {
        return (
            <NotFoundContent
                primaryTo={ROUTES.ADMIN_PRODUCTS}
                primaryLabel="Back to products"
            />
        );
    }

    if (readStatus === "failed") {
        return (
            <div className="px-10 text-neutral">
                <ErrorState onRetry={retryProduct} />
            </div>
        );
    }

    return (
        <div className="px-10 text-neutral">
            <Title>{product ? product.name : "Edit Product"}</Title>

            <form noValidate onSubmit={onSave}>
                <ProductForm
                    register={register}
                    errors={errors}
                    watch={watch}
                    categories={categories}
                    imageSlot={
                        <div>
                            {/* Disabled until the product has loaded too: an
                                image mutation before then has nothing to
                                attach to, and refreshProduct would race the
                                initial read. That disabling is silent — the
                                reason line below is for inactivity only. */}
                            <ProductImageManager
                                productId={id}
                                images={product?.images ?? []}
                                onChanged={refreshProduct}
                                disabled={
                                    isProcessing || !canEdit || isInactive
                                }
                            />
                            {isInactive && (
                                <p className="mt-2 text-sm text-gray-500">
                                    Images can&apos;t be changed while the
                                    product is inactive. Reactivate it and
                                    save first.
                                </p>
                            )}
                        </div>
                    }
                    // #476: saved at once, like the images, and re-read
                    // through refreshProduct, which never adopts a stamp. A
                    // COA write does not move the stamp, so the loaded one
                    // still saves after it. Writes are held as the image
                    // controls are (the COA endpoints 404 on an inactive
                    // product too); the download is not.
                    coaSlot={
                        <div>
                            <ProductCoaManager
                                productId={id}
                                coa={product?.coa ?? null}
                                onChanged={refreshProduct}
                                disabled={
                                    isProcessing || !canEdit || isInactive
                                }
                            />
                            {isInactive && (
                                <p className="mt-2 text-sm text-gray-500">
                                    The COA can&apos;t be changed while the
                                    product is inactive. Reactivate it and
                                    save first.
                                </p>
                            )}
                        </div>
                    }
                    // Ruling I2: Stock and the vendor offerings are read-only,
                    // from the product read.
                    stock={product ? productStockTotal(product) : null}
                    isStockLoading={product === null}
                    offeringsSlot={
                        product && (
                            <VendorOfferings
                                offerings={activeBaseOfferings(product)}
                                hasVariants={product.variants.length > 0}
                            />
                        )
                    }
                    // P1/P2: Price and Cost are read-only, from the product
                    // read (em-dashes until it lands).
                    derivedPrice={product}
                    // PopularRank is an update-only field (createProduct has no
                    // such field), so it appears on the EDIT page only.
                    showPopularRank
                    disabled={!canEdit}
                    categoriesState={categoriesState}
                    onRetryCategories={retryCategories}
                />

                <div className="flex items-center justify-center gap-3 mt-6">
                    {/* IsActive toggle replaces the old Deactivate button.
                        Inert until the product has loaded: it defaults to
                        checked, and a flip made before the read lands is
                        overwritten by setIsActive(loaded.isActive). */}
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            className="toggle toggle-primary"
                            aria-label="Active"
                            checked={isActive}
                            disabled={!canEdit}
                            onChange={(event) =>
                                setIsActive(event.target.checked)
                            }
                        />
                        <span className="font-semibold">Active</span>
                    </label>
                </div>

                {categoriesState === "failed" && (
                    <p className="text-sm text-error text-center mt-6">
                        {SAVE_BLOCKED_BY_CATEGORIES}
                    </p>
                )}

                {/* Next to Save, where the admin is when it is refused.
                    Explicit text-warning-content: the light theme's
                    base-content is white (CLAUDE.md theme quirk). Reload is
                    the page read: fresh values and a fresh stamp, and its
                    failure is the page's own couldn't-load cell. It waits
                    for an image write to finish (see refreshProduct). */}
                {hasSaveConflict && (
                    <Alert
                        color={ComponentColor.Warning}
                        className="text-warning-content mt-6"
                    >
                        {SAVE_CONFLICT_MESSAGE}
                        <div className="mt-2">
                            <Button
                                size={ComponentSize.SM}
                                disabled={isProcessing}
                                onClick={() => void retryProduct()}
                            >
                                Reload product
                            </Button>
                        </div>
                    </Alert>
                )}

                <div className="flex justify-center gap-4 my-8">
                    {/* Save waits for the load: onSave falls back to tags []
                        when nothing is loaded, and a PUT of that over a real
                        product wipes them.
                        Button disables on `disabled || loading`, so `loading`
                        alone holds it during the PUT; `disabled` adds the
                        form-holds-the-loaded-product guard (canEdit), the
                        category-list guard and the save-conflict hold. */}
                    <Button
                        type="submit"
                        color={ComponentColor.Primary}
                        loading={isProcessing}
                        disabled={
                            isProcessing ||
                            !canEdit ||
                            categoriesUnavailable ||
                            hasSaveConflict
                        }
                    >
                        Save
                    </Button>
                    <Button
                        type="button"
                        color={ComponentColor.Ghost}
                        className="text-neutral"
                        disabled={isProcessing}
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
};
